import { Space, Drawer, Button, Col, Form, Row, Input, message, Select, Upload, Flex, Avatar, Typography, Divider } from "antd";
import { useEffect, useState } from "react";
import { createInvestmentIdea, updateInvestmentIdea } from "../../api/ai";
import { firebaseConfig, BucketType } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFirebaseFilename, uuid } from "../../utils/comm";
import { CloseOutlined } from "@ant-design/icons";
import { countryTuples } from 'country-region-data';

const InvestmentIdeaDrawView = ({ open, defaultIdea, retryInfo, onClose, onUpdate, editProofOnly = false, actionType = null, }) => {
    const [idea, setIdea] = useState({
        ticker: null,
        triggerTag: null,
        event: null,
        sector: null,
        ideaType: null,
        rating: null,
        suggestion: null,
        description: null,
        weight: null,
        category: null,
        votes: null,
        status: null,
        country: null,
        goaiConviction: null,
        targetPrice: null,
        targetPriceHorizon: null,
        signalPrice: null,
        lockedPerformance: null,
        exitDetail: null,
    });
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [form] = Form.useForm();
    const countries = countryTuples.map(item => {
        return {
            label: item[0].split(',')[0],
            value: item[0],
            flag: item[1]
        }
    })

    useEffect(() => {
        setIdea(defaultIdea ?? {});
        if (defaultIdea && Object.keys(defaultIdea).length > 0) {
            form.setFieldValue('ticker', defaultIdea.ticker);
            form.setFieldValue('triggerTag', defaultIdea.triggerTag);
            form.setFieldValue('targetPrice', defaultIdea.targetPrice);
            form.setFieldValue('event', defaultIdea.event);
            form.setFieldValue('sector', defaultIdea.sector);
            form.setFieldValue('ideaType', defaultIdea.ideaType);
            form.setFieldValue('rating', defaultIdea.rating);
            form.setFieldValue('suggestion', defaultIdea.suggestion);
            form.setFieldValue('description', defaultIdea.description);
            form.setFieldValue('weight', defaultIdea.weight);
            form.setFieldValue('category', defaultIdea.category);
            form.setFieldValue('votes', defaultIdea.votes);
            form.setFieldValue('status', defaultIdea.status);
            form.setFieldValue('country', defaultIdea.country);
            form.setFieldValue('goaiConviction', defaultIdea.goaiConviction);
            form.setFieldValue('targetPriceHorizon', defaultIdea.targetPriceHorizon);
            form.setFieldValue('signalPrice', defaultIdea.signalPrice);
            form.setFieldValue('lockedPerformance', defaultIdea.lockedPerformance ? (defaultIdea.lockedPerformance / 100) : null);

            // 解析 aiReportTxt JSON 字符串
            if (defaultIdea.aiReportTxt) {
                try {
                    const aiReport = JSON.parse(defaultIdea.aiReportTxt);
                    form.setFieldValue('aiReportStopLossPrice', aiReport.stop_loss_price);
                    form.setFieldValue('aiReportTimeHorizonDays', aiReport.time_horizon_days);
                    form.setFieldValue('aiReportSummary', aiReport.summary);
                    form.setFieldValue('aiReportRecommendedAction', aiReport.recommended_action);
                } catch (error) {
                    console.error('Failed to parse aiReportTxt:', error);
                }
            }

            // 解析 exitDetail JSON 字符串
            if (defaultIdea.exitDetail) {
                try {
                    const exitDetail = JSON.parse(defaultIdea.exitDetail);
                    form.setFieldValue('exitDetailReason', exitDetail.reason);
                    form.setFieldValue('exitDetailPrice', exitDetail.price);
                    form.setFieldValue('exitDetailExitedAt', exitDetail.exited_at);
                    form.setFieldValue('exitDetailNote', exitDetail.note);
                } catch (error) {
                    console.error('Failed to parse exitDetail:', error);
                }
            }

            // 初始化 proofOfWork 字段
            form.setFieldValue('proofOfWork', defaultIdea.proof?.proofOfWork ?? []);
        } else if (retryInfo && Object.keys(retryInfo).length > 0) {
            let additional = {};
            try {
                additional = JSON.parse(retryInfo.additional)
            } catch (error) { }
            form.setFieldValue('ticker', retryInfo.ticker);
            form.setFieldValue('triggerTag', retryInfo.triggerTag);
            form.setFieldValue('event', additional?.event);
            form.setFieldValue('sector', retryInfo.category);

            form.setFieldValue('attachments', (additional?.attachments ?? []).map((item, index) => ({
                mimeType: item.mimeType,
                url: [
                    {
                        uid: `${uuid()}-${index}`,
                        name: getFirebaseFilename(item.url),
                        status: 'done',
                        url: item.url,
                        file: {
                            response: {
                                url: item.url
                            }
                        }
                    }
                ]
            })));
        } else {
            resetFields();
        }
    }, [defaultIdea, open])

    const resetFields = () => {
        form.setFieldValue('ticker', null);
        form.setFieldValue('triggerTag', null);
        form.setFieldValue('targetPrice', null);
        form.setFieldValue('targetPriceHorizon', null);
        form.setFieldValue('event', null);
        form.setFieldValue('sector', null);
        form.setFieldValue('ideaType', null);
        form.setFieldValue('rating', null);
        form.setFieldValue('suggestion', null);
        form.setFieldValue('description', null);
        form.setFieldValue('weight', null);
        form.setFieldValue('category', null);
        form.setFieldValue('votes', null);
        form.setFieldValue('status', null);
        form.setFieldValue('attachments', []);
        form.setFieldValue('country', null);
        form.setFieldValue('goaiConviction', null);
        form.setFieldValue('signalPrice', null);
        form.setFieldValue('lockedPerformance', null);
        form.setFieldValue('exitDetail', null);
        form.setFieldValue('proofOfWork', []);
    }

    const onSubmit = () => {
        let params = {};
        if (defaultIdea) {
            // 常规编辑（创建或编辑草稿）
            params = {
                ...idea,
                weight: parseInt(form.getFieldValue('weight') ?? '0'),
                category: form.getFieldValue('category'),
                votes: form.getFieldValue('votes'),
                ideaType: form.getFieldValue('ideaType'),
                ticker: form.getFieldValue('ticker'),
                rating: form.getFieldValue('rating'),
                suggestion: form.getFieldValue('suggestion'),
                description: form.getFieldValue('description'),
                triggerTag: form.getFieldValue('triggerTag'),
                country: form.getFieldValue('country'),
                goaiConviction: form.getFieldValue('goaiConviction'),
                targetPrice: form.getFieldValue('targetPrice'),
                targetPriceHorizon: form.getFieldValue('targetPriceHorizon'),
                signalPrice: form.getFieldValue('signalPrice'),
            };
            // 基础参数
            params.id = idea.id;

            // 根据 actionType 构建不同的参数
            if (actionType === 'submitChange') {
                // 4.4 提交变更审核 (1 → 3)
                params.status = 3;
                params.targetPrice = form.getFieldValue('targetPrice');

                const aiReportStopLossPrice = form.getFieldValue('aiReportStopLossPrice');
                const aiReportTimeHorizonDays = form.getFieldValue('aiReportTimeHorizonDays');
                const aiReportSummary = form.getFieldValue('aiReportSummary');
                const aiReportRecommendedAction = form.getFieldValue('aiReportRecommendedAction');

                if (aiReportStopLossPrice || aiReportTimeHorizonDays || aiReportSummary || aiReportRecommendedAction) {
                    params.aiReportTxt = JSON.stringify({
                        ...JSON.parse(defaultIdea.aiReportTxt),
                        stop_loss_price: aiReportStopLossPrice || '',
                        time_horizon_days: aiReportTimeHorizonDays ? parseInt(aiReportTimeHorizonDays) : '',
                        summary: aiReportSummary || '',
                        recommended_action: aiReportRecommendedAction || ''
                    });
                }
            } else if (actionType === 'submitExit') {
                // 4.7 提交退出审核 (1 → 4)
                params.status = 4;
                const lockedPerformance = form.getFieldValue('lockedPerformance');
                if (lockedPerformance) {
                    params.lockedPerformance = Math.round(parseFloat(lockedPerformance) * 100);
                }

                const exitDetailReason = form.getFieldValue('exitDetailReason');
                const exitDetailPrice = form.getFieldValue('exitDetailPrice');
                const exitDetailExitedAt = form.getFieldValue('exitDetailExitedAt');
                const exitDetailNote = form.getFieldValue('exitDetailNote');

                if (exitDetailReason || exitDetailPrice || exitDetailExitedAt || exitDetailNote) {
                    params.exitDetail = JSON.stringify({
                        reason: exitDetailReason || '',
                        price: exitDetailPrice || '',
                        exited_at: exitDetailExitedAt || '',
                        note: exitDetailNote || ''
                    });
                }
            } else if (actionType === 'directChange') {
                // 4.11 直接变更并批准 (1 → 1)
                params.directChangeApprove = true;
                params.targetPrice = form.getFieldValue('targetPrice');

                const aiReportStopLossPrice = form.getFieldValue('aiReportStopLossPrice');
                const aiReportTimeHorizonDays = form.getFieldValue('aiReportTimeHorizonDays');
                const aiReportSummary = form.getFieldValue('aiReportSummary');
                const aiReportRecommendedAction = form.getFieldValue('aiReportRecommendedAction');

                if (aiReportStopLossPrice || aiReportTimeHorizonDays || aiReportSummary || aiReportRecommendedAction) {
                    params.aiReportTxt = JSON.stringify({
                        ...JSON.parse(defaultIdea.aiReportTxt),
                        stop_loss_price: aiReportStopLossPrice || '',
                        time_horizon_days: aiReportTimeHorizonDays ? parseInt(aiReportTimeHorizonDays) : '',
                        summary: aiReportSummary || '',
                        recommended_action: aiReportRecommendedAction || ''
                    });
                }
            } else if (actionType === 'directExit') {
                // 4.12 直接退出 (1 → 5)
                params.directExit = true;

                const lockedPerformance = form.getFieldValue('lockedPerformance');
                if (lockedPerformance) {
                    params.lockedPerformance = Math.round(parseFloat(lockedPerformance) * 100);
                }

                const exitDetailReason = form.getFieldValue('exitDetailReason');
                const exitDetailPrice = form.getFieldValue('exitDetailPrice');
                const exitDetailExitedAt = form.getFieldValue('exitDetailExitedAt');
                const exitDetailNote = form.getFieldValue('exitDetailNote');

                if (exitDetailReason || exitDetailPrice || exitDetailExitedAt || exitDetailNote) {
                    params.exitDetail = JSON.stringify({
                        reason: exitDetailReason || '',
                        price: exitDetailPrice || '',
                        exited_at: exitDetailExitedAt || '',
                        note: exitDetailNote || ''
                    });
                }
            }

            // 添加 proof 字段（仅当 editProofOnly 为 true 时）
            if (editProofOnly) {
                params.proof = {
                    proofOfWork: form.getFieldValue('proofOfWork') ?? []
                };
            }
        } else {
            var attachments = form.getFieldValue('attachments')?.map(item => {
                let url = item.url;
                if (Array.isArray(url) && url.length > 0) {
                    if (url[0].url) {
                        url = url[0].url;
                    } else if (url[0].file?.response?.url) {
                        url = url[0].file.response.url;
                    } else if (url[0].response?.url) {
                        url = url[0].response.url;
                    }
                }
                return {
                    mimeType: item.mimeType,
                    url: url,
                }
            })
            params = {
                ticker: form.getFieldValue('ticker'),
                triggerTag: form.getFieldValue('triggerTag'),
                event: form.getFieldValue('event'),
                sector: form.getFieldValue('sector'),
                attachments: attachments,
            }
            if (retryInfo) {
                params.id = retryInfo.id;
            }
        }
        if (defaultIdea?.id) {
            setLoading(true);
            updateInvestmentIdea(params).then(res => {
                setLoading(false);
                setIdea({});
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                message.error(String(err));
                setLoading(false);
            });
        } else {
            if (!params.ticker) {
                message.warning('Please input ticker');
                return;
            }
            if (!params.event || !params.event.trim()) {
                message.warning('Please input event');
                return;
            }
            if (!params.triggerTag || !params.triggerTag.trim()) {
                message.warning('Please input trigger tag');
                return;
            }
            setLoading(true);

            createInvestmentIdea(params).then(res => {
                setLoading(false);
                setIdea({});
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                message.error(String(err));
                setLoading(false);
            });
        }

    }

    const handleUpload = (options, subFieldName) => {
        let file = options.file;
        let fileKey = `${file.uid}-${file.name}`; // 使用文件 uid 和名称作为唯一标识
        let fileName = `${uuid()}-${file.name}`;
        let storage = firebaseConfig(String(file.type).includes('video') ? BucketType.video : BucketType.image);
        const storageRef = ref(storage, `/investment_files/${encodeURIComponent(fileName.replace(/[ !@#$%^&*(),?":{}|<>]/g, '-'))}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            "state_changed",
            (snapshot) => {
                // 计算上传进度百分比
                const percent = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                );
                // 更新进度状态
                setUploadProgress(prev => ({
                    ...prev,
                    [fileKey]: percent
                }));
                // 将进度传递给 Upload 组件
                options.onProgress({ percent });
            },
            (err) => {
                message.error(String(err));
                options.onError(err);
                // 清除该文件的进度记录
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[fileKey];
                    return newProgress;
                });
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                    // 构建 fileList 格式的文件对象
                    const fileObj = {
                        uid: file.uid,
                        name: file.name,
                        status: 'done',
                        url: url,
                        file: {
                            response: {
                                url: url
                            }
                        }
                    };
                    // 将文件对象设置为 fileList 数组
                    form.setFieldValue([...subFieldName], [fileObj]);
                    // 触发 Upload 组件的成功回调
                    options.onSuccess({ url }, file);
                    // 清除该文件的进度记录
                    setUploadProgress(prev => {
                        const newProgress = { ...prev };
                        delete newProgress[fileKey];
                        return newProgress;
                    });
                });
            }
        );
    }

    return (
        <div>
            <Drawer
                forceRender={true}
                title={editProofOnly ? 'Edit Proof of Work' : (defaultIdea ? 'Edit Investment Idea' : 'Create Investment Idea')}
                width={560}
                maskClosable
                onClose={onClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={loading}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark={false} >
                    {
                        !editProofOnly && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item name='ticker' label='Ticker'>
                                        <Input placeholder="ticker" allowClear />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )
                    }
                    {
                        defaultIdea ?
                            (!editProofOnly && <>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='ideaType' label='Type'>
                                            <Select options={[
                                                { key: 1, label: 'AI Pick', value: 0 },
                                                { key: 2, label: 'Social Trader', value: 1 },
                                            ]}
                                                placeholder="select type"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='goaiConviction' label='GoAI Conviction'>
                                            <Select options={[
                                                { key: 1, label: 'GoAI Alpha', value: 'GoAI Alpha' },
                                                { key: 2, label: 'High Conviction', value: 'High Conviction' },
                                                { key: 2, label: 'Medium Conviction', value: 'Medium Conviction' },
                                            ]}
                                                placeholder="select conviction"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                {/* targetPrice 在 submitChange 或 directChange 时在状态转换字段中显示 */}
                                {!(actionType === 'submitChange' || actionType === 'directChange') && (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name='targetPrice' label='Target Price'>
                                                <Input placeholder="target price" allowClear />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name='targetPriceHorizon' label='Target Price Horizon'>
                                                <Input placeholder="target price horizon" allowClear />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}
                                {/* 当是 submitChange 或 directChange 时，只显示 targetPriceHorizon */}
                                {(actionType === 'submitChange' || actionType === 'directChange') && (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name='targetPriceHorizon' label='Target Price Horizon'>
                                                <Input placeholder="target price horizon" allowClear />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='signalPrice' label='Signal Price'>
                                            <Input placeholder="signal price" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='country' label='Country'>
                                            <Select
                                                placeholder="country/city select..."
                                                options={countries}
                                                optionRender={(option) => {
                                                    let flag = `http://purecatamphetamine.github.io/country-flag-icons/3x2/${option.data.flag}.svg`;
                                                    return (
                                                        <Space size={[4, 0]}>
                                                            <Avatar src={flag} size={16} />
                                                            <Typography.Text style={{ fontSize: 12 }}>
                                                                <b>{option.data.label}</b>
                                                            </Typography.Text>
                                                        </Space>
                                                    )
                                                }}
                                                allowClear
                                                showSearch />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='rating' label='GoAI Rating'>
                                            <Select options={[
                                                { key: 1, label: '1', value: 1 },
                                                { key: 2, label: '2', value: 2 },
                                                { key: 3, label: '3', value: 3 },
                                                { key: 4, label: '4', value: 4 },
                                                { key: 5, label: '5', value: 5 },
                                            ]}
                                                placeholder="AI rating"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='suggestion' label='Suggestion'>
                                            <Select options={[
                                                { key: 1, label: 'Bullish', value: 0 },
                                                { key: 2, label: 'Bearish', value: 1 },
                                            ]}
                                                placeholder="suggestion"
                                                allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='triggerTag' label='Trigger Tag'>
                                            <Input placeholder="trigger tag" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='weight' label='Weight'>
                                            <Input placeholder="weight" allowClear type='number' />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name='category' label='Category'>
                                            <Input placeholder="category" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name='votes' label='Votes'>
                                            <Input placeholder="votes" allowClear type='number' />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>)
                            :
                            <>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='triggerTag' label='Trigger Tag'>
                                            <Input placeholder="trigger tag" allowClear />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='event' label='Event'>
                                            <Input.TextArea placeholder="event" allowClear rows={5} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name='sector' label='Sector'>
                                            <Input.TextArea placeholder="sector" allowClear rows={5} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.List name={'attachments'}>
                                            {(subFields, subOpt) => (
                                                <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                                                    {subFields.map((subField) => (
                                                        <Flex key={subField.key} style={{ padding: 12, border: '1px dashed #eee' }}>
                                                            <div style={{ flex: 1, marginRight: 12 }}>
                                                                <div style={{ marginBottom: 12 }}>
                                                                    <Form.Item label='File Type' noStyle name={[subField.name, 'mimeType']}>
                                                                        <Select options={[
                                                                            { key: 1, label: 'Application/pdf', value: 'application/pdf' },
                                                                            { key: 2, label: 'Text/plain', value: 'text/plain' },]}
                                                                            placeholder="Attachments type"
                                                                            allowClear />
                                                                    </Form.Item>
                                                                </div>

                                                                <div>
                                                                    <Form.Item
                                                                        label='File Source'
                                                                        noStyle
                                                                        name={[subField.name, 'url']}
                                                                        valuePropName="fileList"
                                                                        getValueFromEvent={(e) => {
                                                                            if (Array.isArray(e)) {
                                                                                return e;
                                                                            }
                                                                            return e?.fileList;
                                                                        }}
                                                                    >
                                                                        <Upload
                                                                            maxCount={1}
                                                                            multiple={false}
                                                                            customRequest={(options) => handleUpload(options, [subField.name, 'url'])}
                                                                        >
                                                                            <Button>Upload File</Button>
                                                                        </Upload>
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                            <CloseOutlined
                                                                onClick={() => {
                                                                    subOpt.remove(subField.name);
                                                                }}
                                                            />
                                                        </Flex>
                                                    ))}
                                                    <Button style={{ width: 200, fontSize: 12 }} type="dashed" onClick={() => subOpt.add()} block>
                                                        + Add File
                                                    </Button>
                                                </div>
                                            )}
                                        </Form.List>
                                    </Col>
                                </Row>
                            </>
                    }
                    {
                        // 状态转换所需字段
                        defaultIdea && !editProofOnly && (
                            <>
                                {/* 提交变更审核 (1 → 3) 或 直接变更并批准 (1 → 1) */}
                                {(actionType === 'submitChange' || actionType === 'directChange') && (
                                    <>
                                        <Divider>Status Transition Fields</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='targetPrice' label='Target Price'>
                                                    <Input placeholder="target price" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name='aiReportStopLossPrice' label='Stop Loss Price'>
                                                    <Input placeholder="e.g., 145.00" allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name='aiReportTimeHorizonDays' label='Time Horizon (Days)'>
                                                    <Input placeholder="e.g., 45" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='aiReportSummary' label='Summary'>
                                                    <Input.TextArea placeholder="summary" rows={2} allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='aiReportRecommendedAction' label='Recommended Action'>
                                                    <Input placeholder="e.g., Buy, Strong Buy, Hold, Sell" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                                {/* 提交退出审核 (1 → 4) 或 直接退出 (1 → 5) */}
                                {(actionType === 'submitExit' || actionType === 'directExit') && (
                                    <>
                                        <Divider>Status Transition Fields</Divider>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item name='lockedPerformance' label='Locked Performance (%)'>
                                                    <Input placeholder="e.g., 12.5 for 12.5%" allowClear type='number' step='0.01' />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailReason' label='Exit Reason' rules={[{ required: true, message: 'Please select exit reason' }]}>
                                                    <Select options={[
                                                        { key: 1, label: 'Bad News', value: 'bad_news' },
                                                        { key: 2, label: 'Target Hit', value: 'target_hit' },
                                                        { key: 3, label: 'Stop Loss', value: 'stop_loss' },
                                                        { key: 4, label: 'Time Expired', value: 'time_expired' },
                                                    ]}
                                                        placeholder="select reason"
                                                        allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailPrice' label='Exit Price'>
                                                    <Input placeholder="e.g., 180.00" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailExitedAt' label='Exited At'>
                                                    <Input placeholder="e.g., 2026-01-20" allowClear type='date' />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name='exitDetailNote' label='Note'>
                                                    <Input placeholder="e.g., 目标价已达成" allowClear />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </>
                        )
                    }
                    {
                        (defaultIdea && editProofOnly) && (
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.List name={'proofOfWork'}>
                                        {(subFields, subOpt) => (
                                            <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Proof of Work</div>
                                                {subFields.map((subField) => (
                                                    <div key={subField.key} style={{ padding: 12, border: '1px dashed #eee', borderRadius: 4 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                            <span style={{ fontWeight: 500 }}>Item {subField.name + 1}</span>
                                                            <CloseOutlined
                                                                onClick={() => subOpt.remove(subField.name)}
                                                                style={{ cursor: 'pointer', color: '#ff4d4f' }}
                                                            />
                                                        </div>
                                                        <Row gutter={16}>
                                                            <Col span={12}>
                                                                <Form.Item label='ID' name={[subField.name, 'id']}>
                                                                    <Input placeholder="id" allowClear type='number' />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={12}>
                                                                <Form.Item label='Title' name={[subField.name, 'title']}>
                                                                    <Input placeholder="title" allowClear />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                        <Form.Item label='Highlight' name={[subField.name, 'highlight']}>
                                                            <Input placeholder="highlight" allowClear />
                                                        </Form.Item>
                                                        <Form.Item label='Content' name={[subField.name, 'content']}>
                                                            <Input.TextArea placeholder="content" allowClear rows={3} />
                                                        </Form.Item>
                                                    </div>
                                                ))}
                                                <Button style={{ width: 200, fontSize: 12 }} type="dashed" onClick={() => subOpt.add()} block>
                                                    + Add Proof Item
                                                </Button>
                                            </div>
                                        )}
                                    </Form.List>
                                </Col>
                            </Row>
                        )
                    }
                </Form>
            </Drawer>
        </div>
    )
}

export default InvestmentIdeaDrawView;